import classNames from 'classnames'
import { CheckCircleSolid, Circle } from 'iconoir-react'
import { type DragEvent, useEffect, useState } from 'react'
import { getName } from 'app/components/LazyContactCell'
import { TaskAssigneeLabel } from 'app/components/tasks/TaskAssigneeLabel'
import { Button } from 'app/components/ui/button'
import { chipBase, hueClasses } from 'app/lib/chipColors'
import { taskDueState } from 'app/lib/taskDueDate'
import { TASK_DRAG_TYPE } from 'lib/taskCalendar'
import { cn } from 'app/lib/utils'
import type { TaskJson } from 'app/types/entities'
import { bg } from 'lib/background'

// The badge recipe, hued like the tasks table's Due groups.
const CHIP_HUE = {
  overdue: { chip: hueClasses.red, ink: `text-(--chip-red-ink)` },
  dueToday: { chip: hueClasses.blue, ink: `text-(--chip-blue-ink)` },
  other: { chip: hueClasses.gray, ink: `text-(--chip-gray-ink)` },
}

function ContactName({ contactId }: { contactId: string }) {
  const [name, setName] = useState<string>()
  useEffect(() => {
    bg(getName(contactId).then(setName))
  }, [contactId])
  return name ? <span className="truncate text-subtle">{name}</span> : null
}

/** `compact`: the month grid and week board chip. `card`: the Day list row. */
export function TaskCalendarChip({
  task,
  variant,
  onOpen,
  onToggleStatus,
  draggable = false,
}: {
  task: TaskJson
  variant: `compact` | `card`
  onOpen: () => void
  onToggleStatus: () => void
  /** The week board's native drag; the month grid drags through react-big-calendar. */
  draggable?: boolean
}) {
  const dueState = taskDueState(task)
  const closed = dueState === `completed`
  const overdue = dueState === `overdue`
  const hue = overdue ? CHIP_HUE.overdue : dueState === `dueToday` ? CHIP_HUE.dueToday : CHIP_HUE.other

  return (
    <div
      data-task-id={task.id}
      draggable={draggable}
      onDragStart={(e: DragEvent) => {
        if (!task.id) return
        e.dataTransfer.setData(TASK_DRAG_TYPE, task.id)
        e.dataTransfer.effectAllowed = `move`
      }}
      className={cn(
        `min-w-0 text-left transition`,
        variant === `compact`
          ? [chipBase, hue.chip, `flex w-full hover:brightness-95`]
          : `flex items-start gap-2 rounded-md border border-default bg-surface px-3 py-2.5 text-body-sm shadow-elevation-1 hover:border-strong`,
        closed && `opacity-60`,
      )}
    >
      <Button
        variant="subtle"
        size="inline"
        role="checkbox"
        aria-checked={closed}
        aria-label={closed ? `Reopen ${task.title}` : `Complete ${task.title}`}
        onClick={onToggleStatus}
        // Inside react-big-calendar's drag handle in the month grid.
        onMouseDown={(e) => e.stopPropagation()}
      >
        {closed ? (
          <CheckCircleSolid className={variant === `compact` ? cn(`size-3`, hue.ink) : `mt-0.5 size-4`} />
        ) : (
          <Circle
            className={
              variant === `compact` ? cn(`size-3`, hue.ink) : classNames(`mt-0.5 size-4`, overdue && `text-red-600`)
            }
          />
        )}
      </Button>
      <Button
        variant="ghost"
        size="inline"
        onClick={onOpen}
        title={task.title}
        className="min-w-0 flex-1 justify-start"
      >
        <div className="flex w-full min-w-0 flex-col text-left">
          <span
            className={classNames(
              `truncate`,
              variant === `compact`
                ? [hue.ink, closed && `line-through`]
                : closed
                  ? `text-minimal line-through`
                  : overdue
                    ? `font-medium text-red-600`
                    : `font-medium text-strong`,
            )}
          >
            {task.title}
          </span>
          {variant === `card` && (
            <div className="flex min-w-0 items-center gap-2 text-body-xs text-subtle [&_span]:truncate">
              {task.contactId && <ContactName contactId={task.contactId} />}
              {task.contactId && <span aria-hidden>·</span>}
              <TaskAssigneeLabel ownerId={task.ownerId} roleId={task.roleId} />
            </div>
          )}
        </div>
      </Button>
    </div>
  )
}
